import { useState } from 'react';
import { Send } from 'lucide-react';
import { useAppSelector } from '@/shared/store/hooks';
import { useAddReportCommentMutation } from '@/features/reports/api/useReportsQueries';
import type { ReportTabsData } from '@/features/reports/types/reports';

export function CommentsTab({ token, data }: { token?: string; data?: ReportTabsData }) {
  const [comment, setComment] = useState('');
  const user = useAppSelector((s) => s.auth.user);
  const addCommentMut = useAddReportCommentMutation(token);

  const canComment = data?.permission === 'ALLOW_COMMENTS';
  const comments = data?.comments || [];

  const submit = () => {
    if (!comment.trim() || !token) return;
    addCommentMut.mutate(
      {
        guestName: [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Guest',
        guestEmail: user?.email || '',
        comment: comment.trim(),
      },
      { onSuccess: () => setComment('') },
    );
  };

  if (!canComment) {
    return (
      <div className="rounded-2xl border border-dashed border-[var(--dash-line)] bg-white px-4 py-16 text-center text-sm font-medium text-[var(--dash-muted)]">
        Comments are not enabled for this report
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[var(--dash-line)] bg-white p-6 shadow-sm">
      <h3 className="m-0 mb-5 text-left text-lg font-bold text-[var(--dash-ink)]">Comments</h3>

      <div className="mb-5 flex max-h-[400px] flex-col gap-4 overflow-y-auto pr-1">
        {comments.length === 0 ? (
          <p className="py-6 text-center text-sm text-[var(--dash-muted)] italic">
            No comments yet. Be the first to leave one.
          </p>
        ) : (
          comments.map((c) => (
            <div key={c._id} className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--dash-brand)] text-sm font-bold text-white">
                {(c.guestName || 'G').charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 rounded-2xl border border-[var(--dash-line)] bg-[var(--dash-soft)]/50 p-4 text-left">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-sm font-bold text-[var(--dash-ink)]">{c.guestName}</span>
                  {c.createdAt ? (
                    <span className="text-xs font-medium text-[var(--dash-muted)]">{c.createdAt}</span>
                  ) : null}
                </div>
                <p className="m-0 text-sm leading-relaxed text-[var(--dash-ink)]">{c.comment}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="relative">
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Type your comment here…"
          rows={2}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
          className="w-full resize-none rounded-2xl border border-[var(--dash-line)] bg-white py-3 pr-16 pl-4 text-sm text-[var(--dash-ink)] shadow-sm transition outline-none focus:border-[var(--dash-brand)] focus:ring-2 focus:ring-[var(--dash-brand)]/15"
        />
        <button
          type="button"
          onClick={submit}
          disabled={!comment.trim() || addCommentMut.isPending}
          className="absolute right-3 bottom-3 inline-flex items-center gap-1.5 rounded-full bg-[var(--dash-brand)] px-4 py-2 text-xs font-semibold text-white shadow-md transition disabled:opacity-50"
        >
          <Send className="h-3.5 w-3.5" />
          Send
        </button>
      </div>
    </div>
  );
}
